<template>
  <VdPage
    class="configurations-list-page"
    title="Remote Configs"
    :subtitle="`(${configurations.length})`"
    :loading="loading"
  >
    <template #actions>
      <VdSwitch :model-value="expandAll" active-text="Expand all" @update:model-value="onExpandAllChanged" />
      <InputText
        ref="searchInputRef"
        v-model="searchTerm"
        class="search-input"
        placeholder="Looking for something..."
        @focusout="forceFocusOnSearchInput"
        @input="forceBlur = false"
      />
    </template>

    <div class="configurations-list">
      <keep-alive v-if="!loading && filteredConfigurations?.length === 0">
        <vd-empty :title="searchTerm ? 'not found' : 'no data'">
          <div v-if="searchTerm">
            <p>Your search for a configuration has no results</p>
          </div>
          <div v-else>
            <p>Sorry, there is no data.</p>
            <p v-if="inWipVersion && canWriteConfigurations">You must add or import classes before continuing</p>
          </div>
          <template v-if="!searchTerm && inWipVersion" #actions>
            <vd-button
              v-if="canReadClassesCatalog"
              type="primary"
              icon-left="fa:arrow-down-to-line"
              @click="onOpenClassCatalog"
            >
              Import from catalog
            </vd-button>
            <vd-button v-if="canWriteClasses" type="secondary" skin="blue" icon-left="fa:add" @click="onNewClass">
              Add Class
            </vd-button>
          </template>
        </vd-empty>
      </keep-alive>
      <div v-else>
        <ConfigurationsListItem
          v-for="item in filteredConfigurations"
          :id="item.technicalName"
          :key="item.technicalName"
          :configuration-class="item"
          :in-wip-version="inWipVersion"
          :game-id="gameId"
          :version-id="versionId"
          :shops="shops"
          :expanded="expandAll || isCurrentHash(item.technicalName)"
          @delete="(ruleId) => onConfigurationRuleDelete(item.technicalName, ruleId)"
        />
      </div>
    </div>
  </VdPage>
</template>

<script lang="ts">
import { VdPage, VdSwitch } from "@voodoo.io/voodoo-ui";
import InputText from "primevue/inputtext";
import { computed, defineComponent, ref } from "vue";
import { useRouter } from "vue-router";

import {
  queryNames,
  useConfigurationsListPageDeleteRuleMutation,
  useConfigurationsListPageQuery,
  useMutationResponse,
} from "@/graphql";
import useMessages from "@/hooks/messages";
import useRouterHash from "@/hooks/router-hash";
import useSearchFilter from "@/hooks/search-filter";
import usePermissions from "@/hooks/use-permissions";
import useShopsManager from "@/modules/Store/hooks/useShopsManager";
import links from "@/router/links";

import { onClickOutside } from "@vueuse/core";
import ConfigurationsListItem from "./components/ConfigurationsListItem/ConfigurationsListItem.vue";

export default defineComponent({
  name: "ConfigurationsListPage",

  components: {
    ConfigurationsListItem,
    VdSwitch,
    VdPage,
    InputText,
  },

  props: {
    gameId: {
      type: String,
      required: true,
    },
    versionId: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    const router = useRouter();

    const { canWriteConfigurations, canWriteClasses, canReadClassesCatalog } = usePermissions();

    const { isCurrentHash, currentHash, setHash } = useRouterHash();
    const { confirm } = useMessages();
    const expandAll = ref(!currentHash.value);

    const { shopsToDisplay: shops } = useShopsManager(props.gameId, props.versionId);

    const onExpandAllChanged = (val: boolean) => {
      expandAll.value = val;
      if (currentHash.value) {
        setHash("");
      }
    };

    const searchInputRef = ref();
    const forceBlur = ref(false);

    onClickOutside(searchInputRef, () => {
      forceBlur.value = true;
      searchInputRef.value.$el.blur();
    });

    const forceFocusOnSearchInput = () => {
      if (searchInputRef.value && !forceBlur.value) {
        searchInputRef.value.$el.focus();
      }
    };

    const { result, loading } = useConfigurationsListPageQuery({ gameId: props.gameId, versionId: props.versionId });

    const inWipVersion = computed(() => result.value?.game?.version?.isWip ?? false);

    const configurations = computed(() => result.value?.game?.version?.configurationClasses ?? []);

    /**
     * Filter the features from the searchTerm
     */
    const { searchTerm, filteredItems: filteredConfigurations } = useSearchFilter(configurations, [
      "displayName",
      "technicalName",
      "description",
    ]);

    /**
     * Delete the configuration rule
     */
    const { mutate: deleteConfigurationRule } = useConfigurationsListPageDeleteRuleMutation({
      refetchQueries: [queryNames.ConfigurationsListPage],
    });

    const onConfigurationRuleDelete = async (classId: string, ruleId: string) => {
      const confirmed = await confirm("Do you really want to delete this configuration rule?");
      if (confirmed) {
        const response = await deleteConfigurationRule({
          gameId: props.gameId,
          versionId: props.versionId,
          classId,
          ruleId,
        });

        useMutationResponse(response, (resp) => resp?.deleteConfigurationRule);
      }
    };

    /**
     * Routes
     */
    const onNewClass = () => {
      router.push({ name: links.games.classes.new });
    };

    const onOpenClassCatalog = () => {
      router.push({ name: links.games.classes.importFromCatalog });
    };

    return {
      configurations,
      expandAll,
      shops,
      loading,
      filteredConfigurations,
      searchTerm,
      onNewClass,
      onOpenClassCatalog,
      onConfigurationRuleDelete,
      inWipVersion,
      onExpandAllChanged,
      isCurrentHash,
      canWriteConfigurations,
      canWriteClasses,
      canReadClassesCatalog,
      forceFocusOnSearchInput,
      searchInputRef,
      forceBlur,
    };
  },
});
</script>
